import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';

import { isFedRamp } from '@atlassian/atl-context';
import type { AIProactivePluginConfig } from '@atlassian/editor-plugin-ai-proactive';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';

import { type EditorPluginAIFeedbackHandlersRef } from './useEditorPluginAIFeedbackHandlers';

export function useEditorPluginAIProactiveEnabled({
	aiGlobalOptInStatus,
}: {
	aiGlobalOptInStatus: AIProactivePluginConfig['aiGlobalOptIn']['status'];
}): boolean {
	const notFedRamp = !isFedRamp();
	const isLivePage = useIsCurrentPageLive();
	const { locale } = useIntl();
	const isLocaleSupported = ['en-US', 'en-GB'].includes(locale);

	return (
		notFedRamp &&
		aiGlobalOptInStatus === 'enabled' &&
		isLocaleSupported &&
		!!fg('platform_editor_ai_proactive_visual_formatting') &&
		!isLivePage
	);
}

export function useEditorPluginAIProactiveConfig({
	aiGlobalOptIn,
	editorPluginAIFeedbackHandlers,
}: {
	aiGlobalOptIn: AIProactivePluginConfig['aiGlobalOptIn'];
	editorPluginAIFeedbackHandlers: React.RefObject<EditorPluginAIFeedbackHandlersRef>;
}): AIProactivePluginConfig {
	return {
		product: 'CONFLUENCE',
		aiGlobalOptIn,
		handleFeedbackSubmission: async ({ sentiment, getAIExperience, editorAttributes }) => {
			try {
				// TODO: EDF-831: Fix and ensure types match with handleFeedbackSubmission
				// @ts-ignore
				editorPluginAIFeedbackHandlers.current?.handleFeedbackClicked({
					sentiment,
					getAIExperience,
					editorAttributes,
				});
				return { status: 'submitted' as const };
			} catch (err) {
				return { status: 'failed' as const };
			}
		},
	};
}
